import { useEffect, useState } from "react";
import { RoutePath } from "@src/interfaces";
import { usePrivySmartWallet, useSwapContext } from "@src/contexts";
import { IntegrationFactory } from "@src/integrations";
import { IntegrationType, IIntegration } from "@src/interfaces";
import {
  Navigation,
  OpenWalletButton,
  PageLayout,
  PickChainButtton,
  Spinner,
} from "@src/components";

interface Props {
  tab: RoutePath;
}

export const HomeView = ({ tab }: Props) => {
  const {
    integrationConfig,
    integrationTypeOverride,
    isMultichain,
    isExternalMode,
    newPurchaseCounter,
    chain,
  } = useSwapContext();
  const { isAuthenticated, login } = usePrivySmartWallet();

  const [integration, setIntegration] = useState<IIntegration | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadIntegration = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use provider state first, then URL parameter, then fallback to Swap
        const integrationType =
          (integrationTypeOverride as IntegrationType) ||
          (integrationConfig.integrationType as IntegrationType) ||
          IntegrationType.Swap;

        // Create integration instance
        const integrationInstance =
          IntegrationFactory.createIntegration(integrationType);
        setIntegration(integrationInstance);
      } catch (err) {
        console.error("Failed to load integration:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load integration",
        );

        // Fallback to default Swap integration
        try {
          const fallbackIntegration = IntegrationFactory.createIntegration(
            IntegrationType.Swap,
          );
          setIntegration(fallbackIntegration);
        } catch (fallbackErr) {
          console.error("Failed to load fallback integration:", fallbackErr);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadIntegration();
  }, [integrationConfig.integrationType, integrationTypeOverride]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Spinner className="w-8 h-8" />
        <div className="text-sm text-text_primary opacity-60">Loading...</div>
      </div>
    );
  }

  // Error state (shouldn't happen often due to fallback)
  if (error && !integration) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-4">
        <div className="text-center">
          <div className="text-lg font-medium text-error_color mb-2">
            Integration Error
          </div>
          <div className="text-sm text-text_primary opacity-60">{error}</div>
        </div>
      </div>
    );
  }

  // Render the integration
  if (integration) {
    return (
      <PageLayout
        navigation={
          <Navigation
            leftItems={
              isExternalMode
                ? [
                    {
                      content: "Buy",
                      path: RoutePath.Buy,
                    },
                  ]
                : [
                    {
                      content: "Buy",
                      path: RoutePath.Buy,
                    },
                    {
                      content: "Sell",
                      path: RoutePath.Sell,
                    },
                    // {
                    //   content: "Integrations",
                    //   path: RoutePath.Integrations,
                    // },
                  ]
            }
            rightItems={[
              {
                content: isMultichain && <PickChainButtton chain={chain} />,
                isCustomContent: true,
              },
              {
                content: !isExternalMode && (
                  <OpenWalletButton
                    isAuthenticated={isAuthenticated}
                    loginFn={login}
                    newPurchaseCounter={newPurchaseCounter}
                  />
                ),
                isCustomContent: true,
              },
            ]}
          />
        }
      >
        <div className="w-full h-full">{integration.render(tab)}</div>
      </PageLayout>
    );
  }

  // Fallback (shouldn't reach here)
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div className="text-sm text-text_primary opacity-60">
        No integration available
      </div>
    </div>
  );
};
